<script lang="ts">
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';
	import { currentLevel, currentLevelKey } from '../../stores/current-level';
	import { derived } from 'svelte/store';
	import ace from 'ace-builds';
	import { dbgIsIdle, dbgState } from '../../game/debugger';
	import { EditorLineHighlight } from '../../utils/editor-line-hilight';
	import ErrorInfo from '../elements/ErrorInfo.svelte';
	import type { World } from '../../game/World';
	const Range = ace.require( 'ace/range' ).Range;

	export let world: World;
	export let value;
	export let disabled: boolean = false;
	export let highlightLine: number | undefined = undefined;

	const dispatcher = createEventDispatcher();
	const id = 'editor-' + Date.now() + '-' + Math.random();

	let editor: ace.Ace.Editor | undefined = undefined;
	let runningLine: EditorLineHighlight;
	let errorLine: EditorLineHighlight;
	let lockedLinesMarker: number | undefined = undefined;

	let saveChanges = true;

	// Derived store: editor text storage key for current level
	const storageKey = derived( currentLevelKey, ( key ) => ( { code: `${key}-code`, cursor: `${key}-cursor` } ) );

	let errorIconHeight = 0;
	let maxLineIndicator = null;
	let maxLineError = false;

	/**
	 * On mount, create the ACE editor.
	 */
	onMount( () => {
		editor = ace.edit( id );
		editor.setTheme( 'ace/theme/cobalt' );
		editor.session.setMode( 'ace/mode/python' );
		editor.renderer.setScrollMargin( 10, 10, 0, 0 );
		editor.addEventListener( 'change', onEditorChange );
		editor.session.on( 'changeScrollTop', updateErrorMarker );
		editor.renderer.on( 'afterUpdate', updateErrorMarker );

		editor.commands.on( 'exec', onEditorCommand );

		runningLine = new EditorLineHighlight( editor, 'debugger-current-line' );
		errorLine = new EditorLineHighlight( editor, 'debugger-error-line' );

		loadCode();
		setTimeout( updateMaxLineMarker, 10 );
	} );

	/**
	 * On destroy, clean up the ACE editor.
	 */
	onDestroy( () => {
		editor.destroy();
	} );

	/**
	 * Update the max line marker.
	 */
	function updateMaxLineMarker() {
		if ( $currentLevel.maxLines ) {
			maxLineIndicator = getLineY( $currentLevel.maxLines );
			maxLineError = world.getCodeLineCount() > $currentLevel.maxLines;
		} else {
			maxLineIndicator = null;
		}
	}

	/**
	 * Get the Y coordinate of the line number. If the line doesn't exist yet, estimate.
	 */
	function getLineY( line: number ) {
		const lines = editor.session.getLength();
		const base = editor.renderer.textToScreenCoordinates( Math.min( lines - 1, line ), 0 ).pageY;
		const offset = Math.max( 0, line - lines + 1 ) * ( editor.renderer as any ).lineHeight;

		return base + offset;
	}

	/**
	 * Callback on editor change. Save changes and cursor location.
	 */
	function onEditorChange() {
		value = editor.getValue();

		updateMaxLineMarker();

		if ( saveChanges ) {
			localStorage.setItem( $storageKey.code, value );
			localStorage.setItem( $storageKey.cursor, JSON.stringify( editor.getCursorPosition() ) );
		}
	}

	/**
	 * Filter editor commands and modify them.
	*/
	function onEditorCommand( e: Event & { command: { readOnly: boolean, name: string }, args: string, } ) {
		if ( e.command.readOnly ) {
			return;
		}

		// Detect backspaces which would delete "back" into the locked zone.
		const isBadBackspace = (
			e.command.name === 'backspace' &&
			editor.getCursorPosition().column === 0 &&
			editor.getCursorPosition().row === $currentLevel.lockedLines
		);

		// Detect edits when the locked zone is selected.
		const badLinesSelected = editor.selection.getAllRanges().some( range => range.start.row < $currentLevel.lockedLines );

		// Reject any change to the locked zone.
		if ( isBadBackspace || badLinesSelected ) {
			e.preventDefault();
			return;
		}

		// If multiple lines selected when # pressed, comment lines.
		if ( e.command.name === 'insertstring' && e.args === '#' && editor.selection.isMultiLine() ) {
			editor.toggleCommentLines()
			e.preventDefault();
			return;
		}
	}

	/**
	 * Update the vertical position of the error marker (if one exists).
	 */
	function updateErrorMarker() {
		if ( ! $dbgState.error || ! editor ) {
			return;
		}

		errorIconHeight = editor.renderer.textToScreenCoordinates( $dbgState.error.line - 1, 0 ).pageY;
	}

	/**
	 * Load the code from localStorage, if available - otherwise fall back on default(s).
	 */
	function loadCode() {
		try {
			saveChanges = false;

			const code = localStorage.getItem( $storageKey.code ) || $currentLevel && $currentLevel.defaultCode || '';
			editor.setValue( code );

			const cursor = JSON.parse( localStorage.getItem( $storageKey.cursor ) ) || { row: 0, column: 0 };
			editor.clearSelection();
			editor.gotoLine( cursor.row + 1, cursor.column + 1, false );

			if ( lockedLinesMarker !== undefined ) {
				editor.session.removeMarker( lockedLinesMarker );
			}

			lockedLinesMarker = editor.session.addMarker(
				new Range( 0, 0, $currentLevel.lockedLines - 1, 0 ),
				'locked-lines',
				'fullLine',
				true
			);

			editor.getSession().setUndoManager( new ace.UndoManager() );
			dispatcher( 'reset' );
		} catch ( e ) {
			console.error( e );
			// Ignore problems while loading code.
		} finally {
			saveChanges = true;
		}
	}

	// Whenever the storage key changes, update content in the ACE editor.
	$: editor && $currentLevelKey && loadCode();

	// Copy "disabled" property to editor as "ReadOnly"
	$: editor && editor.setReadOnly( disabled );

	// Whenever the debugger stops, put focus on the editor.
	dbgIsIdle.subscribe( ( isIdle: boolean ) => isIdle && editor && editor.focus() );

	// Manage highlighting the currently executing line.
	$: highlightLine = $dbgState.file === '/player/code.py' && $dbgState.line || undefined;
	$: runningLine && runningLine.setLine( highlightLine );
	$: errorLine && errorLine.setLine( $dbgState.error && $dbgState.error.line || undefined );
	$: $dbgState.error && updateErrorMarker();
</script>

{#if maxLineIndicator}
	<div class={ `max-lines-marker ${ maxLineError ? 'error' : '' }` } style={ `top:${ maxLineIndicator }px;` }>
	</div>
{/if}

<div class="editor" {id} />

{#if $dbgState.error}
	<ErrorInfo iconHeight={ errorIconHeight } />
{/if}

<style>
	.editor {
		width: 100%;
		height: 100%;
		font-size: 16px;
	}

	.max-lines-marker {
		position: absolute;
		width: 100%;
		border-top: 2px dashed rgba( 255, 255, 255, 0.25 );
		z-index: 100;
	}

	.max-lines-marker.error {
		border-top-color: rgba( 255, 0, 0, 0.5 );
	}

	:global( .locked-lines ) {
		position: relative;
		background-color: rgba(255, 255, 255, 0.11);
		border: 1px solid rgba(255, 255, 255, 0.3);

		display: flex;
		flex-direction: column;
		justify-content: center;
		text-align: right;
	}

	:global( .locked-lines::after ) {
		font-family: 'Font Awesome 5 Free';
		font-weight: 900;
		content: '\f023';
		opacity: 0.2;
		font-size: 20px;
		margin-right: 10px;
  	}
</style>
